import { useState, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Sparkles, Save, Trash2, Loader2, ArrowRight, Edit3, Upload, FileText, X, Eye } from "lucide-react";
import WorkProcessFlowchart from "./WorkProcessFlowchart";

interface Department {
  id: string;
  name: string;
}

interface WorkProcess {
  id: string;
  department_id: string;
  title: string;
  description: string | null;
  flowchart_data: { nodes: any[]; edges: any[] };
  created_at: string;
  updated_at: string;
  renewed_at: string | null;
  is_active: boolean;
  document_path?: string | null;
  document_name?: string | null;
  document_mime_type?: string | null;
  document_size?: number | null;
}

interface WorkProcessEditorProps {
  process: WorkProcess | null;
  departments: Department[];
  onSave: () => void;
  onCancel: () => void;
}

type EditorStep = "describe" | "flowchart";

const EXAMPLE_DESCRIPTIONS = [
  "OTDR-Messung einer Glasfaserstrecke mit Vorlauf- und Nachlauffaser durchführen",
  "Kupferkabel-Zertifizierung nach Cat 6A Standard mit DSX-Messgerät",
  "Stecker-Reinigung und Inspektion vor LWL-Messung",
];

const WorkProcessEditor = ({ process, departments, onSave, onCancel }: WorkProcessEditorProps) => {
  const { user } = useAuth();
  const [step, setStep] = useState<EditorStep>(process?.flowchart_data?.nodes?.length ? "flowchart" : "describe");
  const [title, setTitle] = useState(process?.title || "");
  const [description, setDescription] = useState(process?.description || "");
  const [departmentId, setDepartmentId] = useState(process?.department_id || "");
  const [flowchartData, setFlowchartData] = useState<{ nodes: any[]; edges: any[] }>(
    process?.flowchart_data || { nodes: [], edges: [] }
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Document state
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [existingDocumentName, setExistingDocumentName] = useState<string | null>(process?.document_name || null);
  const [existingDocumentPath, setExistingDocumentPath] = useState<string | null>(process?.document_path || null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [removeDocument, setRemoveDocument] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateFlowchart = async () => {
    if (!title.trim()) {
      toast.error("Bitte geben Sie einen Titel ein");
      return;
    }
    if (!departmentId) {
      toast.error("Bitte wählen Sie eine Abteilung");
      return;
    }
    
    const inputText = description.trim() || title.trim();
    
    if (!inputText) {
      toast.error("Bitte geben Sie eine Prozessbeschreibung ein");
      return;
    }

    setGenerating(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-flowchart', {
        body: { 
          title: title.trim(),
          description: inputText || `${title}: Erstelle einen detaillierten Arbeitsprozess für diese Tätigkeit.`
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Fehler bei der KI-Generierung');
      }

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.nodes || !data.edges) {
        throw new Error('Ungültige Antwort vom Server');
      }

      setFlowchartData({ nodes: data.nodes, edges: data.edges });
      setStep("flowchart");
      toast.success(`Workflow-Plan mit ${data.nodes.length} Schritten und detaillierten TODOs generiert!`);
    } catch (error: any) {
      console.error('Error generating flowchart:', error);
      toast.error(error.message || "Fehler bei der Generierung des Workflow-Plans");
    } finally {
      setGenerating(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Datei ist zu groß (max. 10 MB)");
        return;
      }
      setDocumentFile(file);
      setRemoveDocument(false);
    }
  };

  const handleRemoveDocument = () => {
    setDocumentFile(null);
    setRemoveDocument(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePreviewExistingDocument = async () => {
    if (!existingDocumentPath) return;
    
    try {
      const { data, error } = await supabase.storage
        .from('work-process-documents')
        .download(existingDocumentPath);
      
      if (error) throw error;
      
      const blobUrl = URL.createObjectURL(data);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error previewing document:', error);
      toast.error("Fehler beim Öffnen des Dokuments");
    }
  };

  const uploadDocument = async (processId: string): Promise<{ path: string; name: string; mimeType: string; size: number } | null> => {
    if (!documentFile) return null;
    
    const fileExt = documentFile.name.split('.').pop();
    const fileName = `${processId}/${Date.now()}.${fileExt}`;
    
    const { error: uploadError } = await supabase.storage
      .from('work-process-documents')
      .upload(fileName, documentFile, {
        cacheControl: '3600',
        upsert: true
      });
    
    if (uploadError) throw uploadError;
    
    return {
      path: fileName,
      name: documentFile.name,
      mimeType: documentFile.type,
      size: documentFile.size
    };
  };

  const deleteExistingDocument = async () => {
    if (!existingDocumentPath) return;
    
    try {
      await supabase.storage
        .from('work-process-documents')
        .remove([existingDocumentPath]);
    } catch (error) {
      console.error('Error deleting old document:', error);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Bitte geben Sie einen Titel ein");
      return;
    }
    if (!departmentId) {
      toast.error("Bitte wählen Sie eine Abteilung");
      return;
    }
    if (!user) {
      toast.error("Nicht authentifiziert");
      return;
    }
    if (flowchartData.nodes.length === 0) {
      toast.error("Bitte generieren Sie zuerst ein Flowchart");
      return;
    }

    setSaving(true);
    try {
      let documentData: { path: string; name: string; mimeType: string; size: number } | null = null;
      
      if (process) {
        // Handle document upload/removal for existing process
        if (documentFile) {
          // Delete old document if exists
          if (existingDocumentPath) {
            await deleteExistingDocument();
          }
          documentData = await uploadDocument(process.id);
        }
        
        const updateData: any = {
          title: title.trim(),
          description: description.trim() || null,
          department_id: departmentId,
          flowchart_data: JSON.parse(JSON.stringify(flowchartData)),
          updated_at: new Date().toISOString(),
          renewed_at: new Date().toISOString(),
        };
        
        // Update document fields
        if (documentData) {
          updateData.document_path = documentData.path;
          updateData.document_name = documentData.name;
          updateData.document_mime_type = documentData.mimeType;
          updateData.document_size = documentData.size;
        } else if (removeDocument) {
          await deleteExistingDocument();
          updateData.document_path = null;
          updateData.document_name = null;
          updateData.document_mime_type = null;
          updateData.document_size = null;
        }
        
        const { error } = await supabase
          .from('work_processes')
          .update(updateData)
          .eq('id', process.id);

        if (error) throw error;
        toast.success("Prozess aktualisiert");
      } else {
        // Create new process first to get ID
        const { data: newProcess, error: insertError } = await supabase
          .from('work_processes')
          .insert([{
            title: title.trim(),
            description: description.trim() || null,
            department_id: departmentId,
            flowchart_data: JSON.parse(JSON.stringify(flowchartData)),
            created_by: user.id,
          }])
          .select('id')
          .single();

        if (insertError) throw insertError;
        
        // Upload document if selected
        if (documentFile && newProcess) {
          documentData = await uploadDocument(newProcess.id);
          
          if (documentData) {
            const { error: updateError } = await supabase
              .from('work_processes')
              .update({
                document_path: documentData.path,
                document_name: documentData.name,
                document_mime_type: documentData.mimeType,
                document_size: documentData.size,
              })
              .eq('id', newProcess.id);
            
            if (updateError) throw updateError;
          }
        }
        
        toast.success("Prozess erstellt");
      }

      onSave();
    } catch (error) {
      console.error('Error saving process:', error);
      toast.error("Fehler beim Speichern des Prozesses");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!process) return;
    
    if (!confirm("Möchten Sie diesen Prozess wirklich löschen?")) return;

    setDeleting(true);
    try {
      const { error } = await supabase
        .from('work_processes')
        .update({ is_active: false })
        .eq('id', process.id);

      if (error) throw error;
      toast.success("Prozess gelöscht");
      onSave();
    } catch (error) {
      console.error('Error deleting process:', error);
      toast.error("Fehler beim Löschen des Prozesses");
    } finally {
      setDeleting(false);
    }
  };

  const useExample = (example: string) => {
    setDescription(example);
    if (!title) {
      setTitle(example.split(' ').slice(0, 4).join(' '));
    }
  };

  return (
    <div className="space-y-6">
      {step === "describe" ? (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                KI-Workflow-Generator
              </CardTitle>
              <CardDescription>
                Beschreiben Sie den Arbeitsprozess – die KI erstellt daraus automatisch einen vollständigen Workflow-Plan mit detaillierten TODOs pro Schritt
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="title">Titel *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="z.B. OTDR-Messung durchführen"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="department">Abteilung *</Label>
                  <Select value={departmentId} onValueChange={setDepartmentId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Abteilung wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Prozessbeschreibung *</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={`Beschreiben Sie den Arbeitsprozess Schritt für Schritt, z.B.:

1. Messgerät einschalten und kalibrieren
2. Vorlauf- und Nachlauffaser anschließen
3. Strecke auswählen und messen
4. Ergebnisse prüfen (PASS/FAIL)
5. Bei Fehler: Stecker reinigen und erneut messen
6. Protokoll erstellen und speichern`}
                  rows={8}
                  className="font-mono text-sm"
                />
              </div>

              {/* Document Upload Section */}
              <div className="space-y-2 pt-4 border-t">
                <Label>Dokument anhängen (optional)</Label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Datei auswählen
                  </Button>
                  
                  {(documentFile || (existingDocumentName && !removeDocument)) && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-md">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm truncate max-w-[200px]">
                        {documentFile?.name || existingDocumentName}
                      </span>
                      {existingDocumentPath && !documentFile && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={handlePreviewExistingDocument}
                          title="Vorschau"
                          className="h-6 w-6"
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={handleRemoveDocument}
                        title="Entfernen"
                        className="h-6 w-6 text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Max. 10 MB. Unterstützte Formate: PDF, Word, Excel, PowerPoint, Bilder
                </p>
              </div>

              <Button 
                onClick={generateFlowchart} 
                disabled={generating || !title.trim() || !departmentId}
                className="w-full gap-2"
                size="lg"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    KI generiert Workflow-Plan...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Workflow-Plan mit KI generieren
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Beispiele zum Starten</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                {EXAMPLE_DESCRIPTIONS.map((example, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    className="justify-start text-left h-auto py-2 px-3 text-sm"
                    onClick={() => useExample(example)}
                  >
                    {example}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
            <h4 className="font-medium text-foreground mb-2">Tipps für bessere Ergebnisse:</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Beschreiben Sie jeden Schritt einzeln auf einer neuen Zeile</li>
              <li>Verwenden Sie klare, aktive Formulierungen (z.B. "Gerät kalibrieren")</li>
              <li>Markieren Sie Entscheidungspunkte mit Fragen (z.B. "Ergebnis OK?")</li>
              <li>Erwähnen Sie wichtige Sicherheitshinweise oder Prüfpunkte</li>
            </ul>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={onCancel}>
              Abbrechen
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{title}</h3>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setStep("describe")}
              className="gap-2"
            >
              <Edit3 className="h-4 w-4" />
              Beschreibung ändern
            </Button>
          </div>

          <div className="border rounded-lg bg-muted/30 min-h-[500px]">
            <WorkProcessFlowchart
              flowchartData={flowchartData}
              onChange={setFlowchartData}
            />
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div>
              {process && (
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {deleting ? "Löschen..." : "Löschen"}
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onCancel}>
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                <Save className="h-4 w-4" />
                {saving ? "Speichern..." : "Speichern"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkProcessEditor;
